import { useCallback, useEffect, useRef, useState } from 'react';
import { loadDraft } from '../services/draftService';

const POLL_INTERVAL_MS = 3000;

// Polls the active draft so two browsers on the same account converge.
// etagRef holds the last version this browser saved or applied; the save and
// load handlers keep it updated so our own writes never come back as "remote".
//
// Polling starts PAUSED by default (startPaused): pulling remote versions is
// opt-in via resumeSync, so a second browser can't silently rewrite a live
// draft. Polling can be paused again (pauseSync) and triggered manually
// (syncNow, which works even while paused). Resuming syncs immediately.
//
// Returns the sync state for display:
//   'off'      - no active draft to sync
//   'waiting'  - no server version yet (first save pending, or the deployed
//                backend does not return ETags and sync cannot work)
//   'paused'   - polling paused by the user
//   'checking' - poll in flight
//   'synced'   - last poll confirmed this browser has the latest version
//   'error'    - last poll failed (network/API error, see console)
export const useDraftSync = ({
  enabled, username, draftName, etagRef, onRemoteChange, startPaused = true
}) => {
  const onRemoteChangeRef = useRef(onRemoteChange);
  onRemoteChangeRef.current = onRemoteChange;

  const [status, setStatus] = useState('waiting');
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  const [isPaused, setIsPaused] = useState(startPaused);
  const isPausedRef = useRef(startPaused);
  const pollRef = useRef(null);

  useEffect(() => {
    if (!enabled) return;

    setStatus('waiting');
    setLastSyncedAt(null);
    // A pause (or resume) applies to the draft being synced; each new draft
    // starts back at the default.
    isPausedRef.current = startPaused;
    setIsPaused(startPaused);
    let inFlight = false;

    const poll = async () => {
      if (inFlight) return;
      // Until the first save assigns an ETag the draft may not exist server-side.
      if (!etagRef.current) {
        setStatus('waiting');
        return;
      }
      inFlight = true;
      setStatus('checking');
      const etagAtStart = etagRef.current;
      try {
        const response = await loadDraft(username, draftName, etagAtStart);
        // Apply only fresh remote versions; a response can be stale when a
        // local save landed mid-poll, and our own save echoes back unchanged.
        if (!response.not_modified && etagRef.current === etagAtStart && response.etag !== etagRef.current) {
          onRemoteChangeRef.current(response.draft_data, response.etag);
        }
        setStatus('synced');
        setLastSyncedAt(Date.now());
      } catch (error) {
        console.error('Error checking for remote draft updates:', error);
        setStatus('error');
      } finally {
        inFlight = false;
      }
    };

    pollRef.current = poll;

    const intervalId = setInterval(() => {
      if (!isPausedRef.current) poll();
    }, POLL_INTERVAL_MS);
    return () => {
      clearInterval(intervalId);
      pollRef.current = null;
    };
  }, [enabled, username, draftName, etagRef, startPaused]);

  const syncNow = useCallback(() => {
    pollRef.current?.();
  }, []);

  const pauseSync = useCallback(() => {
    isPausedRef.current = true;
    setIsPaused(true);
  }, []);

  const resumeSync = useCallback(() => {
    isPausedRef.current = false;
    setIsPaused(false);
    pollRef.current?.();
  }, []);

  const displayStatus = isPaused && status !== 'checking' ? 'paused' : status;
  return {
    syncStatus: enabled ? displayStatus : 'off',
    lastSyncedAt,
    isSyncPaused: isPaused,
    pauseSync,
    resumeSync,
    syncNow
  };
};
